import { Modal } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import React, { useEffect, useState } from "react";
import _loadash from "lodash";
import { enqueueSnackbar } from "notistack";
import { postAPICall } from "lib/callApi";
import { USER_CONFIGS, FILTER_PARAMS, DATE_FILTER_OPTIONS } from "lib/constants";
import { useSelector, useDispatch } from "react-redux";
import { loginUserAction } from "redux/slice/users";
import Loader from "utils/Loader";
import DaysCheckboxGroup from "components/DaysCheckboxGroup";
import FilterAutocomplete from "./FilterAutocomplete";

const createModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: window.innerWidth < 600 ? "100%" : 600,
  // height: window.innerHeight < 800 ? 600 : 650,
  overflowX: "auto",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "0.5rem",
  p: 4,
  "& .MuiFormHelperText-root": { color: "#d32f2f" },
};

function FilterModal({
  open,
  handleClose,
  filters,
  changeURLBasedOnFilter,
  options,
  setCreateNewView,
  setOptions,
  defaultDaysOfWeek,
}) {
  const dispatch = useDispatch();
  const loginUserState = useSelector((state) => state.loginUser.loginUserState);
  const [modalFilters, setModalFilters] = useState(filters);
  const [isLoading, setIsLoading] = useState(false);

  const syncOptionsAndFilter = async (_filters) => {
    try {
      setIsLoading(true);
      const response = await postAPICall(FILTER_PARAMS, true, {
        filters: {
          filterLocations: _filters?.location?.map((lo) => lo.id) || [],
          filterTeams: _filters?.team?.map((lo) => lo.id) || [],
          filterRoles: _filters?.role?.map((lo) => lo.id) || [],
          filterUsers: _filters?.user?.map((lo) => lo.userCompanyLinkId) || [],
          filterReportsTo: _filters?.reportsTo?.map((lo) => lo.id) || [],
          dateFilter: _filters?.dateFilter,
        },
      });
      if (response?.data?.success) {
        const user = response.data.data.users;
        const { reportsTo } = response.data.data;
        const role = response.data.data.roles;
        const team = response.data.data.teams;
        const teamLocation = response.data.data.locations;
        const optionObj = {
          team,
          location: teamLocation,
          user,
          role,
          reportsTo,
          dateFilter: DATE_FILTER_OPTIONS,
        };
        const updatedFilter = { daysOfWeek: _filters.daysOfWeek };
        Object.keys(optionObj).forEach((key) => {
          if (Array.isArray(optionObj[key]) && Array.isArray(_filters[key])) {
            const arr1 = optionObj[key].map((obj) => obj.id);
            const arr2 = _filters[key];
            updatedFilter[key] = arr2.filter((arr2Obj) => arr1.includes(arr2Obj.id));
          }
        });
        if (DATE_FILTER_OPTIONS.includes(_filters.dateFilter)) {
          updatedFilter.dateFilter = _filters.dateFilter;
        }
        setModalFilters({ ...updatedFilter });
        setOptions(optionObj);
      } else if (response?.status !== 403) {
        handleClose();
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text:
              response?.response?.data?.message || response.message || "Could not retrieve data!",
          }),
          {
            variant: "custom",
          }
        );
      } else {
        handleClose();
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || error.message || "Could not retrieve data!",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const handleApplyClick = () => {
    if (!isLoading) {
      syncOptionsAndFilter(modalFilters);
    }
  };

  const handleDeleteClick = (key, filter) => {
    if (!isLoading) {
      const newFilters = { ...modalFilters, [key]: filter };
      setModalFilters(newFilters);
      syncOptionsAndFilter(newFilters);
    }
  };

  useEffect(() => {
    if (open) {
      syncOptionsAndFilter(modalFilters, true);
    }
  }, [open]);

  const saveAsDefaultFilter = async () => {
    try {
      const response = await postAPICall(
        `${USER_CONFIGS}/${loginUserState.userCompanyLinkId}`,
        true,
        {
          value: modalFilters,
          name: "DEFAULT_PERFORMANCE_FILTER",
        }
      );
      if (response?.data?.success) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "info",
            title: "Saved",
            text: response?.data?.message || response.message || "Performance filter saved!",
          }),
          {
            variant: "custom",
          }
        );
        dispatch(
          loginUserAction.getLoginUserData({
            ...loginUserState,
            defaultPermissionFilter: JSON.stringify(modalFilters),
          })
        );
        changeURLBasedOnFilter(modalFilters);
        handleClose();
        setCreateNewView(false);
      } else if (response?.status !== 403) {
        handleClose();
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || response.message || "Could not save data!",
          }),
          {
            variant: "custom",
          }
        );
      } else {
        handleClose();
      }
    } catch (error) {
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || error.message || "Could not update data!",
        }),
        {
          variant: "custom",
        }
      );
    }
  };
  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <MDBox sx={createModalStyle}>
        <MDBox sx={{ position: "relative", pointeEvents: isLoading && "none" }}>
          <Loader loading={isLoading} zIndex="10000" />
          <MDTypography
            sx={{ marginBottom: "1rem" }}
            id="modal-modal-title"
            variant="h5"
            component="h2"
          >
            Filters
          </MDTypography>
          <MDBox>
            <MDButton
              color="secondaryBlue"
              onClick={() => {
                syncOptionsAndFilter({
                  dateFilter: filters.dateFilter,
                  location: [],
                  team: [],
                  user: [],
                  role: [],
                  reportsTo: [],
                  daysOfWeek: defaultDaysOfWeek,
                });
              }}
              sx={{ mb: 2 }}
            >
              Reset Filters
            </MDButton>
          </MDBox>
          <MDBox mb={2} sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <FilterAutocomplete
              field="location"
              options={options.location || []}
              label="Location"
              value={modalFilters?.location}
              onDeleteClick={handleDeleteClick}
              onApplyClick={handleApplyClick}
              onChange={(_, filter) => {
                setModalFilters((prev) => ({ ...prev, location: filter }));
              }}
            />

            <FilterAutocomplete
              options={options.team || []}
              label="Team"
              field="team"
              value={modalFilters.team || []}
              onDeleteClick={handleDeleteClick}
              onApplyClick={handleApplyClick}
              onChange={(_, filter) => {
                setModalFilters((prev) => ({ ...prev, team: filter }));
              }}
            />
            <FilterAutocomplete
              label="Role"
              field="role"
              value={modalFilters.role || []}
              onDeleteClick={handleDeleteClick}
              onApplyClick={handleApplyClick}
              onChange={(_, filter) => {
                setModalFilters((prev) => ({ ...prev, role: filter }));
              }}
              options={options?.role || []}
            />
            <FilterAutocomplete
              label="User"
              field="user"
              value={modalFilters?.user || []}
              onDeleteClick={handleDeleteClick}
              onApplyClick={handleApplyClick}
              onChange={(_, filter) => {
                setModalFilters((prev) => ({ ...prev, user: filter }));
              }}
              options={options?.user || []}
            />
            <FilterAutocomplete
              label="Reports To"
              field="reportsTo"
              value={modalFilters?.reportsTo?.length ? modalFilters.reportsTo : []}
              onApplyClick={handleApplyClick}
              onDeleteClick={handleDeleteClick}
              onChange={(_, filter) => {
                setModalFilters((prev) => ({ ...prev, reportsTo: filter }));
              }}
              options={options?.reportsTo?.length ? options?.reportsTo : []}
            />
            <DaysCheckboxGroup
              value={modalFilters?.daysOfWeek ? modalFilters.daysOfWeek : defaultDaysOfWeek}
              onChange={setModalFilters}
            />
          </MDBox>
          <MDBox sx={{ display: "flex", justifyContent: "space-between" }}>
            <MDBox>
              <MDButton
                disabled={_loadash.isEqual(
                  modalFilters,
                  JSON.parse(loginUserState?.defaultPermissionFilter || "{}")
                )}
                variant="outlined"
                color="gray80"
                sx={{ mt: 2, mr: 1, position: "relative" }}
                onClick={saveAsDefaultFilter}
              >
                Save as default view
              </MDButton>
            </MDBox>
            <MDBox>
              <MDButton
                color="gray80"
                onClick={handleClose}
                sx={{ mt: 2, mr: 1, position: "relative" }}
              >
                Close
              </MDButton>
              <MDButton
                color="primaryRed"
                onClick={() => {
                  changeURLBasedOnFilter(modalFilters);
                  handleClose();
                }}
                sx={{ mt: 2, mr: 1, position: "relative" }}
              >
                Apply
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </MDBox>
    </Modal>
  );
}

export default FilterModal;
